import { ref, computed, watch, nextTick } from 'vue';

export const COLOR_LIBRE        = '#0CBA31';
export const COLOR_OCUPADO      = '#CC0000';
export const COLOR_SIN_PRECIO   = 'gray';
export const COLOR_SELECCIONADO  = '#1E40AF';

/**
 * useSvgSeatMap — Lógica de mapa SVG 3D de butacas (tipo_svg=1).
 *
 * Recibe las props reactivas del componente padre y gestiona:
 *  - Parseo del SVG string e inyección de colores/click handlers.
 *  - Búsqueda de círculos por ID de butaca.
 *  - Sincronización bidireccional: selección del store → colores SVG,
 *    y clicks en círculos → emit de toggle/quitar asiento.
 *
 * @param {import('vue').DefinePropsResult} props  Props reactivas del componente padre:
 *   svgString, asientosData, asientoSeleccionado
 * @param {Function} emit  Función emit del componente padre
 * @returns {{ svgParsed, svgPreparando, isPanning, onIsPanning, deselectSeat, COLOR_LIBRE }}
 */
export function useSvgSeatMap(props, emit) {

    // ─── State ────────────────────────────────────────────────────────────

    const svgParsed = ref(null);
    const isPanning = ref(false);

    /**
     * `true` mientras el SVG string ya llegó pero el parseo interno aún no ha
     * poblado `svgParsed` (el watch hace `prepararSvg` dentro de `nextTick`).
     * Permite al componente mostrar un spinner de carga en lugar del empty
     * state ("sin butacas") durante ese micro-task. Vuelve a `false` tras el
     * intento de parseo, haya tenido éxito (→ `svgParsed`) o no (→ `null`).
     */
    const svgPreparando = ref(false);

    /** Mapa: seat.id → svg_id del círculo en el SVG */
    let svgIdPorSeatId = null;

    // ─── Computed ─────────────────────────────────────────────────────────

    /**
     * Aplana configuracion_asientos a array plano con svg_id para el matching SVG.
     */
    const seatsFlat = computed(() => {
        const conf = props.asientosData?.configuracion_asientos;
        if (!conf || typeof conf !== 'object') return [];
        const result = [];
        const entries = Array.isArray(conf) ? conf : Object.entries(conf);
        for (const [filaKey, asientos] of entries) {
            const lista = Array.isArray(asientos) ? asientos : Object.values(asientos ?? {});
            for (const a of lista) {
                result.push(a);
            }
        }
        return result;
    });

    // ─── SVG DOM helpers ──────────────────────────────────────────────────

    /**
     * Localiza el círculo del SVG que corresponde a una butaca.
     *
     * Los ids reales del SVG son multi-parte (`butaca-101-0`, `butaca-0143-6-6-1`)
     * y el SEGUNDO segmento es el svg_id. `prepararSvg` rellena `svgIdPorSeatId`
     * con `s.svg_id` tal cual llega del backend (número o string) y hace match
     * contra ese mismo segundo segmento con `String()`, así que aquí se replica
     * ese criterio. Se añade un fallback numérico por si el backend entrega el
     * svg_id sin padding de ceros (`143`) mientras el SVG usa el id con ceros
     * (`butaca-0143-...`): con comparación numérica ambos coinciden.
     */
    function buscarCirculoPorSeat(seat) {
        if (!svgParsed.value) return null;
        const svgId = svgIdPorSeatId?.[seat.id];
        if (svgId == null) return null;
        const circles = svgParsed.value.querySelectorAll('[id^="butaca-"]');
        const numericSvgId = Number(svgId);
        for (const c of circles) {
            const seg = String(c.id.split('-')[1] ?? '');
            if (seg === String(svgId)) return c;
            const numericSeg = Number(seg);
            if (Number.isFinite(numericSeg) && Number.isFinite(numericSvgId) && numericSeg === numericSvgId) {
                return c;
            }
        }
        return null;
    }

    function onClickSeat(seatId) {
        const seat = seatsFlat.value.find(s => s.id === seatId);
        if (!seat) return;

        const isSelected = props.asientoSeleccionado?.id === seatId;
        if (isSelected) {
            emit('quitar-asiento');
        } else {
            emit('toggle-asiento', seat);
        }
    }

    /**
     * Construye el SVG interactivo: colorea círculos (libre/ocupado/sin-precio)
     * y añade click handlers que emiten toggle/quitar al padre.
     */
    function prepararSvg(svgStr, seats) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = svgStr;
        const svgEl = tempDiv.firstElementChild;
        if (!svgEl || svgEl.tagName !== 'svg') return null;

        // Índice: seat.id → seat + svg_id
        const seatsById = {};
        const svgIdBySeat = {};
        for (const s of seats) {
            if (s.id) {
                seatsById[s.id] = s;
                if (s.svg_id) svgIdBySeat[s.id] = s.svg_id;
            }
        }
        svgIdPorSeatId = svgIdBySeat;

        // Recorrer círculos del SVG
        const circles = svgEl.querySelectorAll('[id^="butaca-"]');
        circles.forEach(circle => {
            const svgId = circle.id.split('-')[1];

            let seat = null;
            for (const id in svgIdBySeat) {
                if (String(svgIdBySeat[id]) === String(svgId)) {
                    seat = seatsById[id];
                    break;
                }
            }

            if (!seat) {
                circle.style.display = 'none';
                return;
            }

            const disponible = seat.ocupado === 0;
            const tienePrecio = seat.precios_count > 0;

            if (disponible && tienePrecio) {
                circle.style.fill = COLOR_LIBRE;
                circle.setAttribute('fill', COLOR_LIBRE);
                circle.style.cursor = 'pointer';
                circle.style.transition = 'fill 0.2s';

                circle.addEventListener('click', e => {
                    e.stopPropagation();
                    if (isPanning.value) return;

                    // Delegación pura de estado: el pintado/restauración lo hacen
                    // exclusivamente los watchers sobre `props.asientoSeleccionado`.
                    // Pintar aquí crea una doble fuente de verdad y desincroniza el
                    // atributo `seleccionado` del círculo con el estado Vue (bug: tras
                    // quitar desde la X, un segundo click volvía a desmarcar).
                    onClickSeat(seat.id);
                });
            } else if (disponible) {
                circle.setAttribute('fill', COLOR_SIN_PRECIO);
            } else {
                circle.setAttribute('fill', COLOR_OCUPADO);
            }
        });

        return svgEl;
    }

    // ─── Watches ──────────────────────────────────────────────────────────

    /**
     * Inyecta los click handlers en el SVG cuando ambos (SVG string + butacas) están listos.
     * `svgPreparando` cubre la ventana entre que llega el SVG y `prepararSvg`
     * puebla `svgParsed` (dentro de `nextTick`), evitando el flash de empty
     * state ("sin butacas") mientras el mapa se está montando.
     */
    watch([() => props.svgString, () => props.asientosData], ([svgStr, data], _old, onCleanup) => {
        if (!svgStr || !data?.configuracion_asientos) {
            svgPreparando.value = false;
            return;
        }

        svgPreparando.value = true;
        let active = true;
        onCleanup(() => { active = false; svgParsed.value = null; svgPreparando.value = false; });

        nextTick(() => {
            if (!active) return;
            const el = prepararSvg(svgStr, seatsFlat.value);
            svgPreparando.value = false;
            if (el) svgParsed.value = el;
        });
    }, { immediate: true });

    /**
     * Sincroniza colores del SVG cuando la selección cambia desde el padre.
     */
    watch(() => props.asientoSeleccionado, (nuevo, anterior) => {
        if (!svgParsed.value) return;

        // Restaurar color de la butaca anterior
        if (anterior) {
            const circle = buscarCirculoPorSeat(anterior);
            if (circle) {
                const color = (anterior.ocupado === 0 && anterior.precios_count > 0)
                    ? COLOR_LIBRE : COLOR_SIN_PRECIO;
                circle.style.fill = color;
                circle.setAttribute('fill', color);
                circle.setAttribute('seleccionado', 'false');
            }
        }

        // Colorear la nueva butaca
        if (nuevo) {
            const circle = buscarCirculoPorSeat(nuevo);
            if (circle) {
                circle.style.fill = COLOR_SELECCIONADO;
                circle.setAttribute('fill', COLOR_SELECCIONADO);
                circle.setAttribute('seleccionado', 'true');
            }
        }
    });

    /**
     * Aplica el color de selección cuando el SVG recién se parsea y ya hay una butaca seleccionada.
     */
    watch(svgParsed, svg => {
        if (svg && props.asientoSeleccionado) {
            const circle = buscarCirculoPorSeat(props.asientoSeleccionado);
            if (circle) {
                circle.style.fill = COLOR_SELECCIONADO;
                circle.setAttribute('fill', COLOR_SELECCIONADO);
                circle.setAttribute('seleccionado', 'true');
            }
        }
    });

    // ─── Interacción ──────────────────────────────────────────────────────

    function onIsPanning(val) {
        isPanning.value = val;
    }

    function deselectSeat() {
        emit('quitar-asiento');
    }

    // ─── API ──────────────────────────────────────────────────────────────

    return {
        svgParsed,
        svgPreparando,
        isPanning,
        onIsPanning,
        deselectSeat,
        COLOR_LIBRE,
    };
}
